import {RunningTimesOffPeriodsWithoutDetail} from '@domain/running-times'
import {ApiClient} from '@hconnect/apiclient'
import {
  fetchMainEquipmentByPxTrendTagSuggestion,
  getQueryKeyMainEquipmentByPxTrendTagSuggestion
} from '@hconnect/common/services'
import {
  EquipmentData,
  RunningTimeDto as RunningTime,
  StoppageDto as Stoppage
} from '@hconnect/common/types'
import CloseIcon from '@mui/icons-material/Close'
import {Box, IconButton, Stack, Typography} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import {generateStoppageDtoDefaultData} from '@widgets/running-times'
import {StoppageCreate} from '@widgets/shift-event-details'
import React, {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {DowntimeAnalyticsEventName, FEATURE_NAMESPACE} from '../consts'
import {useTrackDowntimeDashboardAnalytics} from '../hooks'
import {useDowntimePlantId} from '../providers'

import {AmendStoppagesVariantSwitch, StoppageSwitchVariant} from './AmendStoppagesVariantSwitch'
import {NoMoreStoppages} from './NoMoreStoppages'
import {StoppageListNavigatorTable} from './StoppageListNavigatorTable'

interface StoppagesListNavigatorProps {
  stoppages: RunningTimesOffPeriodsWithoutDetail[]
  onViewChange: (view: StoppageSwitchVariant) => void
  removeStoppage: (stoppage: RunningTimesOffPeriodsWithoutDetail) => void
  isLoading: boolean
}

export const StoppagesListNavigator: React.FC<StoppagesListNavigatorProps> = ({
  stoppages,
  onViewChange,
  removeStoppage,
  isLoading
}) => {
  const plantId = useDowntimePlantId()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const queryClient = useQueryClient()
  const trackDowntimeDashboardAnalytics = useTrackDowntimeDashboardAnalytics(plantId)

  const [selectedStoppageState, setSelectedStoppageState] =
    useState<RunningTimesOffPeriodsWithoutDetail | null>(null)
  const [stoppageInitialData, setStoppageInitialData] = useState<Partial<Stoppage> | undefined>(
    undefined
  )

  const setInitialData = useCallback(
    async (stoppage: RunningTimesOffPeriodsWithoutDetail) => {
      let suggestedMainEquipment: EquipmentData | undefined = undefined
      try {
        suggestedMainEquipment = await queryClient.fetchQuery({
          queryKey: getQueryKeyMainEquipmentByPxTrendTagSuggestion(plantId, stoppage.equipment.id),
          queryFn: async () =>
            fetchMainEquipmentByPxTrendTagSuggestion({
              plantId,
              pxTrendTag: stoppage.equipment.id,
              axiosInstance: ApiClient.axiosInstance
            }),
          staleTime: 5000
        })
      } catch (error) {
        console.error('Error fetching main equipment by px trend tag suggestion', error)
      }

      const runningTime: RunningTime = stoppage.runningTime
      const val = generateStoppageDtoDefaultData({
        runningTime,
        equipment: stoppage.equipment,
        suggestedMainEquipment
      })
      setStoppageInitialData(val)
    },
    [plantId, queryClient]
  )

  const setSelectedStoppage = useCallback(
    (stoppage: RunningTimesOffPeriodsWithoutDetail | null) => {
      // on selection of new stoppage its necessary to clear init data otherwise previous will linger
      // causing wrong initialization of stoppage form
      setStoppageInitialData(undefined)
      if (stoppage) {
        trackDowntimeDashboardAnalytics({
          name: DowntimeAnalyticsEventName.UserOpenStoppageToAmendForm,
          data: {equipmentId: stoppage.equipment.id}
        })
        void setInitialData(stoppage)
      }
      setSelectedStoppageState(stoppage)
    },
    [setInitialData, trackDowntimeDashboardAnalytics]
  )

  if (!isLoading && !stoppages.length) {
    return <NoMoreStoppages onClose={() => onViewChange(null)} />
  }

  if (selectedStoppageState && stoppageInitialData) {
    return (
      <Box height="100%" overflow="hidden">
        <StoppageCreate
          key={`${selectedStoppageState.equipment.id}-${selectedStoppageState.runningTime.beginTechnical}`}
          plantId={plantId}
          data-test-id="stoppages-list-navigator"
          doClose={() => setSelectedStoppage(null)}
          initialData={stoppageInitialData}
          onSuccessSubmit={() => {
            trackDowntimeDashboardAnalytics({
              name: DowntimeAnalyticsEventName.UserAmendStoppage,
              data: {equipmentId: selectedStoppageState.equipment.id}
            })
            setSelectedStoppage(null)
            removeStoppage(selectedStoppageState)
          }}
          bodyWithPadding={false}
          onError={(error) => {
            // If stoppage already exists, remove it from the list
            // 409 is the status code for conflict
            if (error.response?.status === 409) {
              removeStoppage(selectedStoppageState)
            }
          }}
          renderFormHeaderActions={({doClose}) => (
            <IconButton
              onClick={() => doClose()}
              color="primary"
              data-test-id="stoppage-form-close"
              size="medium"
            >
              <CloseIcon />
            </IconButton>
          )}
          withSalesImpact={false}
          withPriority
          withCategory
          eventTypeLabelPrefix={selectedStoppageState.equipment.text}
        />
      </Box>
    )
  }

  return (
    <Stack height="100%">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h3">{t('newStoppagesBanner.label.stoppageList')}</Typography>
        <AmendStoppagesVariantSwitch value="list" onChange={onViewChange} />
      </Stack>
      <Box my={2} height="100%" sx={{overflowY: 'auto'}}>
        <StoppageListNavigatorTable
          stoppages={stoppages}
          setSelectedStoppage={setSelectedStoppage}
        />
      </Box>
    </Stack>
  )
}
